import {
	ResExportDefinition,
	ResParameter,
} from "@shapediver/sdk.geometry-api-sdk-v2";
import {
	Gradient,
	ISelectionParameterProps,
	OrthographicCameraProperties,
	PerspectiveCameraProperties,
	SessionCreationDefinition,
	TAG3D_JUSTIFICATION,
} from "@shapediver/viewer.shared.types";
import type {
	IAppBuilderActionDefinition,
	IAppBuilderActionPropsCommon,
	IAppBuilderActionPropsCreateModelState,
	IAppBuilderActionPropsSetParameterValue,
	IAppBuilderLegacyActionDefinition,
	AppBuilderSetParameterValuesUpdateMode as SetParameterValuesUpdateMode,
} from "./appbuilderActions";
import type {
	IAppBuilderNodeActionSlots,
	IAppBuilderRootActionSlots,
} from "./appbuilderActionSlots";
import type {IAppBuilderAgent} from "./appbuilderagent";
import {
	IAppBuilderWidgetPropsAreaChart,
	IAppBuilderWidgetPropsBarChart,
	IAppBuilderWidgetPropsLineChart,
	IAppBuilderWidgetPropsRoundChart,
} from "./appbuildercharts";
import type {IAppBuilderColor} from "./appbuilderColor";

export {
	isAddToCartAction,
	isAnimateCameraAction,
	isArAction,
	isAssignCameraAction,
	isCameraAction,
	isCloseConfiguratorAction,
	isCreateModelStateAction,
	isExecuteActionsAction,
	isExportParameterValuesAction,
	isFullscreenAction,
	isImportModelStateAction,
	isImportParameterValuesAction,
	isMessageToParentAction,
	isRedoAction,
	isResetCameraAction,
	isResetParameterValuesAction,
	isSetBrowserLocationAction,
	isSetCameraAction,
	isSetContainerVisibilityAction,
	isSetParameterValueAction,
	isSetParameterValuesAction,
	isSoundAction,
	isUndoAction,
	isZoomToCameraAction,
} from "./appbuilderActions";
export type {
	IAppBuilderActionDefinition,
	IAppBuilderActionPropsAddToCart,
	IAppBuilderActionPropsAr,
	IAppBuilderActionPropsCamera,
	IAppBuilderActionPropsCloseConfigurator,
	IAppBuilderActionPropsCommon,
	IAppBuilderActionPropsCreateModelState,
	IAppBuilderActionPropsExecuteActions,
	IAppBuilderActionPropsExportParameterValues,
	IAppBuilderActionPropsFullscreen,
	IAppBuilderActionPropsImportModelState,
	IAppBuilderActionPropsImportParameterValues,
	IAppBuilderActionPropsMessageToParent,
	IAppBuilderActionPropsRedo,
	IAppBuilderActionPropsResetParameterValues,
	IAppBuilderActionPropsSetBrowserLocation,
	IAppBuilderActionPropsSetContainerVisibility,
	IAppBuilderActionPropsSetParameterValue,
	IAppBuilderActionPropsSetParameterValues,
	IAppBuilderActionPropsSound,
	IAppBuilderActionPropsUndo,
	IAppBuilderLegacyActionDefinition,
	IAppBuilderLegacyActionPropsAddToCart,
	IAppBuilderLegacyActionPropsAr,
	IAppBuilderLegacyActionPropsCamera,
	IAppBuilderLegacyActionPropsCloseConfigurator,
	IAppBuilderLegacyActionPropsCreateModelState,
	IAppBuilderLegacyActionPropsExecuteActions,
	IAppBuilderLegacyActionPropsExportParameterValues,
	IAppBuilderLegacyActionPropsFullscreen,
	IAppBuilderLegacyActionPropsImportModelState,
	IAppBuilderLegacyActionPropsImportParameterValues,
	IAppBuilderLegacyActionPropsMessageToParent,
	IAppBuilderLegacyActionPropsRedo,
	IAppBuilderLegacyActionPropsResetParameterValues,
	IAppBuilderLegacyActionPropsSetBrowserLocation,
	IAppBuilderLegacyActionPropsSetContainerVisibility,
	IAppBuilderLegacyActionPropsSetParameterValue,
	IAppBuilderLegacyActionPropsSetParameterValues,
	IAppBuilderLegacyActionPropsSound,
	IAppBuilderLegacyActionPropsUndo,
	IAppBuilderPropsAnimateCamera,
	IAppBuilderPropsAssignCamera,
	IAppBuilderPropsResetCamera,
	IAppBuilderPropsSetCamera,
	IAppBuilderPropsZoomToCamera,
} from "./appbuilderActions";
export type {
	AppBuilderActionSlotEventPropsType,
	AppBuilderApplicationEvent,
	AppBuilderCustomEventName,
	AppBuilderInteractionEvent,
	AppBuilderKnownEvent,
	AppBuilderUiEvent,
	IAppBuilderActionSlot,
	IAppBuilderActionSlotEventProps,
	IAppBuilderActionSlotEventPropsExport,
	IAppBuilderActionSlotEventPropsSelection,
	IAppBuilderActionSlotEventPropsSession,
	IAppBuilderActionSlotList,
	IAppBuilderActionSlots,
	IAppBuilderExportActionSlot,
	IAppBuilderNodeActionSlots,
	IAppBuilderPlainActionSlot,
	IAppBuilderRootActionSlots,
	IAppBuilderSelectionActionSlot,
	IAppBuilderSessionActionSlot,
} from "./appbuilderActionSlots";
export {AppBuilderActionType} from "./appBuilderActionType";
export type {IAppBuilderColor};
export type AppBuilderSetParameterValuesUpdateMode =
	SetParameterValuesUpdateMode;

/** How a string text input commits values to the session. */
export enum ParameterStringInputMode {
	Debounce = "debounce",
	Validate = "validate",
}

/**
 * Inline Iconify icon object (SVG body plus optional viewBox / transform fields).
 * Stand-in for Iconify `IconifyIcon` so this contract does not import `@iconify/*`.
 */
export interface IAppBuilderIconifyIcon {
	/** Icon body: `<path d="..." />`, required. */
	body: string;
	left?: number;
	top?: number;
	width?: number;
	height?: number;
	rotate?: number;
	hFlip?: boolean;
	vFlip?: boolean;
}

/**
 * Icon name (e.g. `"tabler:photo"`), image URL (http(s), data URI, or path;
 * SVG and common raster formats), or inline Iconify icon object.
 */
export type IAppBuilderIcon = string | IAppBuilderIconifyIcon;

/** JSON-facing slider mark. `label` is a string, not a React node. */
export interface IAppBuilderSliderMark {
	value: number;
	label?: string;
	hidden?: boolean;
}

/** Type used for parameter definitions */
export type IAppBuilderParameterDefinition = ResParameter & {
	/**
	 * The value to set for the generic parameter. Use this to update
	 * the parameter's current value (i.e. its state) without changing the
	 * parameter definition.
	 * In case no value is defined when creating a new generic parameter,
	 * the new parameter's value is set to the default value defined in the
	 * parameter definition.
	 */
	value?: string;

	/**
	 * @deprecated use settings.step instead
	 * Optional step value for numeric parameters.
	 */
	step?: number;

	settings?: {
		/**
		 * When true, render a reset button on the parameter control that
		 * restores the parameter's default value. Defaults to false.
		 * Color parameters always show a reset button regardless of this flag.
		 * JSON: `overrides.settings.resettable`.
		 */
		resettable?: boolean;
	};
};

/** Type used for export definitions */
export type IAppBuilderExportDefinition = ResExportDefinition;

/** Types of selection components. */
export type SelectComponentType =
	| "buttonflex"
	| "buttongroup"
	| "chipgroup"
	| "dropdown"
	| "color"
	| "imagedropdown"
	| "fullwidthcards"
	| "carousel"
	| "grid"
	| "multiselect-checkboxes";

/** Data for an item shown by a selection component. */
export interface ISelectComponentItemDataType {
	/** Display name to use instead of the item name. */
	displayname?: string;
	/** Tooltip. */
	tooltip?: string;
	/** Description. */
	description?: string;
	/** URL to image. Can be a data URL including a base 64 encoded image. */
	imageUrl?: string;
	/** Optional color, used for color selection components. Mantine theme name, `color.shade`, hex, or other CSS color. */
	color?: IAppBuilderColor;
	/** Optionally hide the item. */
	hidden?: boolean;
	/**
	 * Optional additional data that can be sent to a String parameter
	 * represented by a selection component, instead of the selected item value.
	 */
	data?: Record<string, any>;
}

/** Settings for selection parameters (typically used for parameters of type "StringList") */
export interface ISelectParameterSettings {
	/** Type of select component to use. */
	type?: SelectComponentType;
	/** Record containing optional further item data per item name. */
	itemData?: Record<string, ISelectComponentItemDataType>;
	/** Enable search for string list inputs (only for type=="dropdown"). */
	searchable?: boolean;
	/** Max number of options rendered at the same time (only for type=="dropdown"). Default: 5 if searchable is enabled */
	limit?: number;
	/** Optional CSS controlling the absolute height of the widget.
	 * In case this is not specified, the default behavior of the widget
	 * is to adapt its height according to the items.
	 */
	height?: string;
	/**
	 * Settings of the optional filterable database to fetch items from.
	 * Currently supported for type "fullwidthcards" and "grid".
	 */
	database?: IFilterableDatabaseSettings;
}

/** Settings of a filterable database component. */
export interface IFilterableDatabaseSettings {
	/**
	 * Source of the data to be displayed in the filterable database component.
	 * This is expected to be a CSV or JSON file, fetched either from a public URL
	 * or from an export defined in a session.
	 * CSV rows are tabular (column index 0, 1, 2, …).
	 * JSON rows are objects: top-level keys match `itemDataDefinition`
	 * (`value`, `displayname`, `imageUrl`, …); custom fields live under `"data"`
	 * (same keys as `itemDataDefinition.data`).
	 * Values of multi-valued columns must be separated by semicolons (or JSON arrays).
	 */
	dataSource: {
		/** Optional reference to export which provides the CSV file. */
		export?: Pick<IAppBuilderExportRef, "name" | "sessionId">;
		/** URL to fetch CSV file from. Takes precedence over export reference. */
		href?: string;
		/** Data file format. Inferred from href suffix when omitted. */
		format?: "csv" | "json";
	};
	/**
	 * Definition of how the itemData shall be extracted from the data source.
	 * Indices are 0-based.
	 */
	itemDataDefinition: {
		/**
		 * Index of the column that holds the value committed to the parameter
		 * (typically column 0). Must match StringList choices or the String value
		 * you intend to store.
		 */
		value: number;
		/** Optional index of the column to use for "displayname". */
		displayname?: number;
		/** Optional index of the column to use for "tooltip". */
		tooltip?: number;
		/** Optional index of the column to use for "description". */
		description?: number;
		/** Optional index of the column to use for "imageUrl". */
		imageUrl?: number;
		/**
		 * Optional index of the column to use for "color".
		 * The color must be given in a format compatible with Mantine (theme name, `color.shade`, hex, or other CSS color).
		 */
		color?: number;
		/**
		 * Optional record of column indices to use for composing the additional
		 * "data" property, that is sent to the String parameter represented by the
		 * selection component, instead of the selected item value.
		 */
		data?: Record<string, number>;
	};
	/**
	 * Array of filter definitions, defining which filters are shown and how
	 * they are applied to the data source, resulting in updates to the "items"
	 * and "itemData" properties.
	 */
	filters: {
		/** Index of the column to filter (option values and matching / selection keys). */
		column: number;
		/**
		 * Optional 0-based column index for per-option *text*. Matching and
		 * selection keys stay on `column`. Omit to show the value as the label.
		 * Out-of-range indices behave like an empty cell.
		 * Distinct from `label`, which is the filter group / accordion title.
		 */
		columnLabel?: number;
		/**
		 * Optional display name for the filter group in the UI (accordion title,
		 * active tag group context). Not used as option text — use `columnLabel`
		 * for per-option display names. Defaults to `Filter ${index + 1}` when omitted.
		 */
		label?: string;
		/**
		 * Set this to true if the column contains multiple values separated by semicolons,
		 * and the filter should check if any of the values matches the selected filter value(s).
		 */
		multivalued?: boolean;
		/**
		 * Set this to true if the user should be allowed to pick multiple filter values at
		 * the same time, and the filter should check if any of the values matches any of
		 * the selected filter values.
		 */
		multiple?: boolean;
		/**
		 * Optional type of the filter, defining how the filter values are shown to the user.
		 * `color` — swatch + tag list; `text` — free-text substring match on the column.
		 * Multi-select tag groups (`multiple !== false`, not `text`) show a master "All"
		 * checkbox that selects or clears every option in the group.
		 */
		type?: "color" | "text";
		/**
		 * When `true`, render the filter directly in the dropdown without an accordion
		 * section. Defaults to `false` (accordion Control/Panel). Works for all filter types.
		 */
		inline?: boolean;
		/**
		 * Optional array of filter values to show to the user. If this is not provided, the
		 * filter values are automatically extracted from the data source. Note that this
		 * might have performance implications for large data sources.
		 * For multivalued columns, the filter values are extracted from splitting the column
		 * values by semicolons.
		 */
		filterValues?: string[];
	}[];
}

/**
 * Settings for string parameters visualized as selection parameters.
 * In this case, the selected item is set as the string value of the parameter.
 */
export interface IStringParameterSelectSettings extends ISelectParameterSettings {
	/**
	 * The items to select from.
	 * In case this is not specified, "source" or "database" must be given.
	 */
	items?: string[];
	/**
	 * Name of the optional "data source" to fetch "items" and "itemData" from.
	 * This is used for connecting to data sources via the e-commerce API.
	 * Currently, this is only supported for type "fullwidthcards" and "grid".
	 */
	source?: string;
	/**
	 * Settings of the optional "database" to fetch "items" and "itemData" from.
	 * A filterable database component will be used to show the items and allow
	 * the user to filter them.
	 * Currently, this is only supported for type "fullwidthcards" and "grid".
	 */
	database?: IFilterableDatabaseSettings;
}

/** Settings for parameters of type "String" */
export interface IStringParameterSettings {
	/** Number of lines to display. If > 1, a Textarea is used with autosize and fixed rows. Default: 1 */
	lines?: number;
	/**
	 * Debounce delay in milliseconds before recomputing after text changes.
	 * Same meaning as theme `ParameterStringComponent.defaultProps.debounce`.
	 * Per-parameter override; wins over theme.
	 */
	debounce?: number;
	/**
	 * How the string text input commits values to the session.
	 * Same meaning as theme `ParameterStringComponent.defaultProps.mode`.
	 * Per-parameter override; wins over theme.
	 */
	mode?: ParameterStringInputMode;
	/**
	 * Optional selection settings.
	 * If this is specified, the parameter is visualized as a selection parameter.
	 * In this case, the selected item is set as the string value of the parameter.
	 */
	selectSettings?: IStringParameterSelectSettings;
}

/** Settings for numeric parameters (type "Float", "Int", "Even", "Odd") */
export interface INumberParameterSettings {
	/** Marks displayed on the slider track. */
	marks?: IAppBuilderSliderMark[];
	/** When true, selection is restricted to the given marks. */
	restrictToMarks?: boolean;
	/** Step increment for the slider. */
	step?: number;
	/** Override the minimum value of the slider (can only increase the parameter's min). */
	min?: number;
	/** Override the maximum value of the slider (can only decrease the parameter's max). */
	max?: number;
}

/** Reference to a parameter (custom or defined by the session) */
export interface IAppBuilderParameterRef {
	/** Id or name or displayname of the referenced parameter (in that order). */
	name: string;
	/** Optional id of the session the referenced parameter belongs to. */
	sessionId?: string;
	/** Properties of the parameter to be overridden. */
	overrides?: Pick<
		Partial<IAppBuilderParameterDefinition>,
		| "displayname"
		| "group"
		| "order"
		| "tooltip"
		| "hidden"
		| "settings"
		| "step"
	>;
	/** Disable the UI element of the parameter if its state is dirty. */
	disableIfDirty?: boolean;
	/** Ask the user to accept or reject changes of this parameter before executing them. */
	acceptRejectMode?: boolean;
	/**
	 * Where accept/reject controls are shown when `acceptRejectMode` is enabled.
	 * "global" uses the shared viewport, toolbar, or widget buttons.
	 * "inline" shows Accept and Reject inside this parameter's control, and only
	 * while that parameter has a queued change.
	 * Defaults to "global".
	 */
	acceptRejectModePresentation?: "global" | "inline";
}

/** Reference to an export (defined by the session) */
export interface IAppBuilderExportRef {
	/** Id or name or displayname of the referenced export (in that order). */
	name: string;
	/** Optional id of the session the referenced parameter belongs to. */
	sessionId?: string;
	/** Properties of the export to be overridden. */
	overrides?: Pick<
		Partial<IAppBuilderExportDefinition>,
		"displayname" | "group" | "order" | "tooltip" | "hidden"
	>;
}

/**
 * Neutral node-level fields shared by widgets, tabs, containers, controls,
 * and toolbar items. Future node-level properties (for example style,
 * SS-9919) belong here, not inside widget `props`.
 *
 * The root {@link IAppBuilder} does not extend this. Its slots also include
 * application events.
 *
 * @see [Action slots](../action-slots.md)
 */
export interface IAppBuilderNode {
	/**
	 * Actions assigned to events emitted by this node.
	 * Pointer, click, contextmenu, and `custom:*` only.
	 * Zod rejects any other event name, and `eventProps`.
	 *
	 * @see [Action slots](../action-slots.md)
	 */
	actionSlots?: IAppBuilderNodeActionSlots;
}

/** An App Builder control. */
export interface IAppBuilderControl extends IAppBuilderNode {
	/** Type of the control. */
	type: AppBuilderControlType;
	/** Properties of the control. */
	props:
		| IAppBuilderControlParameterRef
		| IAppBuilderControlExportRef
		| IAppBuilderControlActionRef
		| IAppBuilderControlOutputRef;
}

/** Types of controls */
export type AppBuilderControlType =
	| "parameter"
	| "export"
	| "action"
	| "output";

/**
 * Presentation of a control or toolbar panel (widget/button or toolbar icon).
 * `labelSide` / `labelAlign` apply only on a toolbar; ignored elsewhere.
 */
export interface IAppBuilderControlPresentation {
	label?: string;
	/** Icon name, image URL, or inline Iconify object. */
	icon?: IAppBuilderIcon;
	tooltip?: string;
	/** When set with a non-empty label, show the caption on this side of the icon. */
	labelSide?: AppBuilderToolbarSide;
	/** Cross-axis alignment of the icon caption. Default "center". */
	labelAlign?: AppBuilderToolbarAlign;
}

/** Control referencing a parameter (custom or defined by the session) */
export interface IAppBuilderControlParameterRef extends IAppBuilderControlPresentation {
	/** Id or name or displayname of the referenced parameter (in that order). */
	name: string;
	/** Optional id of the session the referenced parameter belongs to. */
	sessionId?: string;
	/** Properties of the parameter to be overridden. */
	overrides?: Pick<
		Partial<IAppBuilderParameterDefinition>,
		"displayname" | "tooltip" | "hidden" | "settings" | "step"
	>;
	/** Disable the UI element of the parameter if its state is dirty. */
	disableIfDirty?: boolean;
	/** Ask the user to accept or reject changes of this parameter before executing them. */
	acceptRejectMode?: boolean;
	/**
	 * Where accept/reject controls are shown when `acceptRejectMode` is enabled.
	 * "global" uses the shared viewport, toolbar, or widget buttons.
	 * "inline" shows Accept and Reject inside this parameter's control, and only
	 * while that parameter has a queued change.
	 * Defaults to "global".
	 */
	acceptRejectModePresentation?: "global" | "inline";
	/** Identifiers of the parameters that shall be updated in addition. */
	delegates: Array<Pick<IAppBuilderParameterRef, "name" | "sessionId">>;
}

/** Control referencing an export (defined by the session) */
export interface IAppBuilderControlExportRef extends IAppBuilderControlPresentation {
	/** Id or name or displayname of the referenced export (in that order). */
	name: string;
	/** Optional id of the session the referenced export belongs to. */
	sessionId?: string;
	/** Properties of the export to be overridden. */
	overrides?: Pick<
		Partial<IAppBuilderExportDefinition>,
		"displayname" | "tooltip" | "hidden"
	>;
	/**
	 * The parameter values that should be used for the export.
	 * These parameter values must belong to the same session as the export.
	 */
	parameterValues?: IAppBuilderActionPropsSetParameterValue[];
}

/** Control referencing an output (defined by the session) */
export interface IAppBuilderControlOutputRef extends IAppBuilderControlPresentation {
	/** Id or name or displayname of the referenced output (in that order). */
	name: string;
	/** Optional id of the session the referenced output belongs to. */
	sessionId?: string;
	/** Properties of the output to be overridden. */
	overrides?: Pick<
		Partial<IAppBuilderExportDefinition>,
		"displayname" | "tooltip" | "hidden"
	>;
}

/** Control referencing an action */
export interface IAppBuilderControlActionRef
	extends
		IAppBuilderActionPropsCommon,
		Pick<IAppBuilderControlPresentation, "labelSide" | "labelAlign"> {
	/** Embedded action definition. */
	definition: IAppBuilderActionDefinition;
	/** In the future we might include a reference to a globally defined action here.  */
}

/** Reference to an image */
export interface IAppBuilderImageRef {
	/** Optional reference to export which provides the image. */
	export?: Pick<IAppBuilderExportRef, "name" | "sessionId">;
	/** URL to image. Can be a data URL including a base 64 encoded image. Takes precedence over export reference. */
	href?: string;
}

/** Types of parameter value sources */
export type AppBuilderParameterValueSourceType =
	| "screenshot"
	| "dataOutput"
	| "export"
	| "sdtf"
	| "modelState"
	| "agentTool";

/**
 * Properties for the "screenshot" parameter value source.
 * This parameter value source is compatible with parameters of type "File".
 * The specified contentType must be supported by the respective "File"	parameter.
 */
export interface IAppBuilderParameterValueSourcePropsScreenshot {
	/**
	 * Optional type of the screenshot, defaults to "image/png".
	 * @see https://viewer.shapediver.com/v3/latest/api/interfaces/IViewportApi.html#getScreenshot
	 */
	contentType?: string;
	/**
	 * Optional quality of the screenshot, between 0 and 1, defaults to 1.
	 * @see https://viewer.shapediver.com/v3/latest/api/interfaces/IViewportApi.html#getScreenshot
	 */
	quality?: number;
	/**
	 * Optional resolution of the screenshot, defaults to the current resolution of the viewport.
	 * TODO SS-8346 define type
	 */
	resolution?: {width: number; height: number};
	/**
	 * Optional camera settings to be used for the screenshot. Defaults to the current camera of the viewport.
	 * If a "name" is provided, the settings of the camera with that name are used as a base.
	 */
	camera?:
		| Partial<OrthographicCameraProperties>
		| Partial<PerspectiveCameraProperties>;
}

export interface IAppBuilderParameterValueSourcePropsCommon {
	/** Id of the session to use for finding the parameter value source. Defaults to the controller session. */
	sessionId?: string;
	/** Id or name or displayname of the referenced parameter value source (in that order). */
	name: string;
}

/**
 * Properties for the "dataOutput" parameter value source.
 * This parameter value source is compatible with parameters of type "String" and "File".
 * For "File" parameters, the content type "application/json" is used.
 */
export type IAppBuilderParameterValueSourcePropsDataOutput =
	IAppBuilderParameterValueSourcePropsCommon;

/**
 * Properties for the "export" parameter value source.
 * This parameter value source is compatible with parameters of type "File".
 * The content type of the exported file must be supported by the "File" parameter.
 */
export interface IAppBuilderParameterValueSourcePropsExport extends IAppBuilderParameterValueSourcePropsCommon {
	/**
	 * Parameter set tha is used in the session to get the parameter value source.
	 * Defined in a parameter dictionary where the key is either the displayname, the name or the id of the parameter.
	 * The value is the parameter value.
	 * If none is provided, the default parameter set is used.
	 **/
	parameterValues?: {
		[key: string]: IAppBuilderParameterValueDefinition;
	};
}

/**
 * Properties for the "sdtf" parameter value source.
 * This parameter value source is compatible with parameters of type "s*".
 *
 * Note: The specified chunk must be compatible with the parameter type,
 * otherwise no data will be set in Grasshopper.
 *
 * @see https://help.shapediver.com/doc/sdtf-structured-data-transfer-format#sdTF-Structureddatatransferformat-Chunkselectionlogic
 */
export interface IAppBuilderParameterValueSourcePropsSdtf extends IAppBuilderParameterValueSourcePropsCommon {
	/**
	 * Optional, defines chunk to be used.
	 * @see https://help.shapediver.com/doc/sdtf-structured-data-transfer-format#sdTF-Structureddatatransferformat-Advancedcase
	 */
	chunk?: {
		/** Id of the chunk to be used. */
		id?: string;
		/** Name of the chunk to be used. */
		name?: string;
	};
}

/**
 * Properties for the "modelState" parameter value source.
 * A new model state will be created according to the properties.
 * This parameter value source is compatible with parameters of type "String".
 *
 * The parameter value resulting from this source is the current location (URL),
 * including the following query parameters:
 *   * modelStateId: the id of the created model state
 *   * other query parameters defined in the current URL, except for UTM parameters
 */
export interface IAppBuilderParameterValueSourcePropsModelState extends IAppBuilderActionPropsCreateModelState {
	/**
	 * Whether the URL shown in the browser shall be updated
	 * with the newly created modelStateId.
	 */
	updateUrl?: boolean;
}

/**
 * Properties for the "agentTool" parameter value source.
 * This source is used for actions that are triggered by an agent tool.
 */
export interface IAppBuilderParameterValueSourcePropsAgentTool {
	/**
	 * Path to one value in the agent tool input.
	 * `length`, `shelf.x`, or `items[0]`. A leading `$.` is accepted.
	 */
	path: string;
}

/** Definition of a parameter value source. */
export interface IAppBuilderParameterValueSourceDefinition {
	/** Type of the parameter value source. */
	type: AppBuilderParameterValueSourceType;
	/** Properties of the parameter value source, depending on type. */
	props:
		| IAppBuilderParameterValueSourcePropsScreenshot
		| IAppBuilderParameterValueSourcePropsDataOutput
		| IAppBuilderParameterValueSourcePropsExport
		| IAppBuilderParameterValueSourcePropsSdtf
		| IAppBuilderParameterValueSourcePropsModelState
		| IAppBuilderParameterValueSourcePropsAgentTool;
}

/** Type used for parameter value definitions */
export type IAppBuilderParameterValueDefinition =
	| string
	| number
	| boolean
	| IAppBuilderParameterValueSourceDefinition;

/** assert parameter source */
export function isParameterSource(
	source: IAppBuilderParameterValueSourceDefinition,
): source is IAppBuilderParameterValueSourceDefinition {
	return (
		isDataOutputSource(source) ||
		isExportSource(source) ||
		isSdtfSource(source) ||
		isModelStateSource(source) ||
		isScreenshotSource(source)
	);
}

/** assert source type "dataOutput" */
export function isDataOutputSource(
	source: IAppBuilderParameterValueSourceDefinition,
): source is {
	type: "dataOutput";
	props: IAppBuilderParameterValueSourcePropsDataOutput;
} {
	return source.type === "dataOutput";
}

/** assert source type "export" */
export function isExportSource(
	source: IAppBuilderParameterValueSourceDefinition,
): source is {
	type: "export";
	props: IAppBuilderParameterValueSourcePropsExport;
} {
	return source.type === "export";
}

/** assert source type "sdtf" */
export function isSdtfSource(
	source: IAppBuilderParameterValueSourceDefinition,
): source is {
	type: "sdtf";
	props: IAppBuilderParameterValueSourcePropsSdtf;
} {
	return source.type === "sdtf";
}

/** assert source type "modelState" */
export function isModelStateSource(
	source: IAppBuilderParameterValueSourceDefinition,
): source is {
	type: "modelState";
	props: IAppBuilderParameterValueSourcePropsModelState;
} {
	return source.type === "modelState";
}

/** assert source type "screenshot" */
export function isScreenshotSource(
	source: IAppBuilderParameterValueSourceDefinition,
): source is {
	type: "screenshot";
	props: IAppBuilderParameterValueSourcePropsScreenshot;
} {
	return source.type === "screenshot";
}

/** Types of widgets */
export type AppBuilderWidgetType =
	| "accordion"
	| "text"
	| "image"
	| "roundChart"
	| "lineChart"
	| "areaChart"
	| "barChart"
	| "actions"
	| "attributeVisualization"
	| "agent"
	| "hostedAgent"
	| "iframe"
	| "progress"
	| "desktopClientSelection"
	| "desktopClientOutputs"
	| "controls"
	| "form"
	| "accordionUi"
	| "savedStates"
	| "sceneTreeExplorer"
	| "stackUi"
	| "table";

/**
 * Properties of a parameter and export accordion widget.
 * UI elements of the referenced parameters and exports are grouped
 * and ordered according to their properties (which might be overridden).
 */
export interface IAppBuilderWidgetPropsAccordion {
	/** References to parameters which shall be displayed by the accordion. */
	parameters?: IAppBuilderParameterRef[];
	/** References to exports which shall be displayed by the accordion. */
	exports?: IAppBuilderExportRef[];
	/**
	 * Optional name of group that should be used for all parameters/exports without a group.
	 * In case this is not specified, parameters/exports without a group will be displayed without an accordion.
	 */
	defaultGroupName?: string;
}

/** Properties of a text widget. */
export interface IAppBuilderWidgetPropsText {
	/** Plain text. Takes precedence. */
	text?: string;
	/** Optional markdown. */
	markdown?: string;
}

export interface IAppBuilderWidgetPropsAnchor {
	/** Follow link. */
	anchor?: string;
	/** Optional reference to specifies where to open the linked document which provides the image, "_blank" by default */
	target?: string;
}

/** Properties of an image widget. */
export interface IAppBuilderWidgetPropsImage
	extends IAppBuilderWidgetPropsAnchor, IAppBuilderImageRef {
	/** Optional reference to alternate text which provides the image. */
	alt?: string;
	/**
	 * Optional boolean to indicate that the widget shall render the image as an SVG.
	 * In case this is not specified, the widget will detect whether the image is an
	 * SVG based on its href.
	 */
	isSvg?: boolean;
}

/** Properties of a widget presenting actions. */
export interface IAppBuilderWidgetPropsActions {
	/** The actions. */
	actions?: IAppBuilderLegacyActionDefinition[];
}

/** Properties of a widget presenting controls. */
export interface IAppBuilderWidgetPropsControls {
	/** The controls. */
	controls?: IAppBuilderControl[];
}

/** Submit behavior for form widget */
export enum FormWidgetSubmitBehavior {
	NONE = "none",
	RESET = "reset",
	MESSAGE = "message",
}

/** Properties of a form widget. */
export interface IAppBuilderWidgetPropsForm {
	/** The controls to display in the form. */
	controls?: IAppBuilderControl[];
	/** References to parameters which shall be displayed by the form. */
	parameters?: IAppBuilderParameterRef[];
	/** Export to trigger on form submission (supports export-control fields such as parameterValues). */
	export?: IAppBuilderControlExportRef;
	/** What to do after successful form submission. */
	submit?: FormWidgetSubmitBehavior;
	/** Success message to display (when submit is "message"). */
	successMessage?: string;
	/** Error message to display (when submit is "message"). */
	errorMessage?: string;
}

/** Enum of the visibility of the attribute visualization. */
export enum AttributeVisualizationVisibility {
	/** The attribute visualization is always enabled. */
	// AlwaysOn = "alwaysOn",
	/** The attribute visualization is enabled by default, but can be turned off. */
	DefaultOn = "defaultOn",
	/** The attribute visualization is disabled by default, but can be turned on. */
	DefaultOff = "defaultOff",
}

export type SavedStatesVisualization = Extract<
	SelectComponentType,
	| "buttonflex"
	| "buttongroup"
	| "chipgroup"
	| "dropdown"
	| "imagedropdown"
	| "fullwidthcards"
	| "carousel"
	| "grid"
>;

/** Properties of a widget then attribute visualization. */
export interface IAppBuilderWidgetPropsAttributeVisualization {
	/** Title for the widget (default: "Attributes") */
	title?: string;
	/** Tooltip for the widget (default: "") */
	tooltip?: string;
	/** List of attributes to be visualized.
	 *  Either a string or an object with the attribute name and an optional gradient.
	 *  If a string is provided, the attribute will be visualized with a default gradient.
	 */
	attributes?: (
		| string
		| {
				attribute: string;
				gradient?: Gradient;
		  }
	)[];
	/** Enable the attribute visualization by default. (default: AttributeVisualizationVisibility.DefaultOff) */
	visualizationMode?: AttributeVisualizationVisibility;
	/** Show the legend, if there is one. (default: true) */
	showLegend?: boolean;
	/** Default gradient, that should be used if none is supplied in the definition of the attribute. (default: TODO) */
	defaultGradient?: Gradient;
	/** Initial attribute that is displayed (default: first attribute in the list) */
	initialAttribute?: string;
	/** Material definition for objects that don't have the selected attribute. (default: { color: "#666666", opacity: 1 }) */
	passiveMaterial?: {
		/** Color of the material. (default: "#000") */
		color?: string;
		/** Opacity of the material. (default: 0.1) */
		opacity?: number;
	};
	/** Option to disable the anchors when clicking on an attribute. (default: false) */
	disableAttributeAnchors?: boolean;
}

/**
 * Properties of the in-page OpenAI chat widget (`type: "agent"`).
 * Distinct from hosted AppBuilderAgent (`type: "hostedAgent"`).
 */
export interface IAppBuilderWidgetPropsAgent {
	/** Additional context. */
	context?: string;
	/** Names of parameters to include in agent workflow. */
	parameterNames?: string[];
	/** Names of parameters to exclude in agent workflow. */
	parameterNamesExclude?: string[];
}

/**
 * Properties of a hosted AppBuilderAgent widget (`type: "hostedAgent"`).
 * The agent URL is resolved from the environment; this widget has no `url`.
 */
export interface IAppBuilderWidgetPropsHostedAgent {
	/** Accessible name for the frame. Default: ShapeDiver agent. */
	title?: string;
	/**
	 * Height of the frame. A number is pixels.
	 * Examples: 400, "24rem", "100%". Default: 24rem.
	 */
	height?: string | number;
	// /**
	//  * Id of an `IAppBuilder.agents` entry to host.
	//  * When omitted, `agents[0]` is used.
	//  */
	// agentId?: string;
}

/**
 * Properties of an iframe widget.
 * `url` is an absolute http(s) page.
 */
export interface IAppBuilderWidgetPropsIframe {
	/** Absolute http(s) URL. */
	url: string;
	/** Accessible name for the frame. */
	title?: string;
	/**
	 * Height of the frame. A number is pixels.
	 * Examples: 400, "24rem", "100%". Default: 24rem.
	 */
	height?: string | number;
}

/** Properties of a progress widget. */
export interface IAppBuilderWidgetPropsProgress {
	/** Option to show the progress bar when completed. (default: false) */
	showOnComplete?: boolean;
	/** Delay the removal of the last progress details (in milliseconds). (default: 1500) */
	delayRemoval?: number;
	/** Option to show the progress message. (default: false) */
	showMessages?: boolean;
	/** Option to show the progress percentage. (default: true) */
	showPercentage?: boolean;
}

/** Properties of a scene tree explorer widget. */
export interface IAppBuilderWidgetPropsSceneTreeExplorer {
	__placeholder?: never; // This is a placeholder to ensure that this interface is not empty.
}

/** Properties of a desktop client selection widget. */
export interface IAppBuilderWidgetPropsDesktopClientSelection {
	clientsFilter?: string[]; // allowed client names that can be filtered out.
	autoConnect?: boolean; // if true and a single client is detected, it will be automatically connected.
}

/** Properties of a desktop client outputs widget. */
export interface IAppBuilderWidgetPropsDesktopClientOutputs {
	__placeholder?: never; // This is a placeholder to ensure that this interface is not empty.
}

/** Properties of a saved states widget. */
export interface IAppBuilderWidgetPropsSavedStates {
	visualization?: SavedStatesVisualization;
}

/**
 * Properties of a generic accordion widget, grouping further widgets
 * into an accordion.
 */
export interface IAppBuilderWidgetPropsAccordionUi {
	/** Items of the accordion. */
	items: {
		/**
		 * Optional unique identifier for the accordion item.
		 * Used to identify items when controlling state of the accordion.
		 */
		value?: string;
		/** Label shown for the accordion control of the item. */
		name: string;
		/** Optional icon name, image URL, or inline Iconify object of the accordion control. */
		icon?: IAppBuilderIcon;
		/** Optional tooltip for the accordion control of the item. */
		tooltip?: string;
		/** Widgets displayed in the accordion item. */
		widgets: IAppBuilderWidget[];
	}[];
	/** If set, multiple items can be opened at the same time. */
	multiple?: boolean;
	/**
	 * Optional default state of the accordion items.
	 * Only used for the initial state of the accordion.
	 */
	defaultValue?: string | string[];
	/**
	 * Optional state of the accordion items.
	 * Used to override the current state of the accordion.
	 */
	value?: string | string[];
}

/**
 * Properties of a stack widget, grouping further widgets
 * into a stack.
 */
export interface IAppBuilderWidgetPropsStackUi {
	/** Label shown for the stack control. */
	name: string;
	/** Optional icon name, image URL, or inline Iconify object of the stack control. */
	icon?: IAppBuilderIcon;
	/** Optional tooltip of the stack control. */
	tooltip?: string;
	/** Widgets displayed in the stack. */
	widgets: IAppBuilderWidget[];
}

/** Definition of a single column in the table widget. */
export interface IAppBuilderWidgetPropsTableColumn {
	/** Column accessor key — must match a field name in the records. */
	accessor: string;
	/** Optional display title for the column header. Defaults to the accessor. */
	title?: string;
	/** Allow sorting by this column. */
	sortable?: boolean;
	/** Show a search/filter input for this column. */
	searchable?: boolean;
	/** Optional fixed column width (number = px, string = CSS value). */
	width?: number | string;
}

/** Properties of a table widget. */
export interface IAppBuilderWidgetPropsTable {
	/** Optional caption displayed below the table. */
	caption?: string;
	/** Column definitions. */
	columns: IAppBuilderWidgetPropsTableColumn[];
	/** Table records (rows). Each record must be an object whose keys match column accessors. */
	records: Record<string, unknown>[];
	/** Highlight row on hover. Default: false. */
	highlightOnHover?: boolean;
	/** Stick the header row to the top when scrolling. Default: false. */
	stickyHeader?: boolean;
	/** Alternate row background color. Default: false. */
	striped?: boolean;
	/** Show borders between columns. Default: false. */
	withColumnBorders?: boolean;
	/** Show borders between rows. Default: true. */
	withRowBorders?: boolean;
	/** Show an outer border around the table. Default: false. */
	withTableBorder?: boolean;
	/**
	 * Fixed height in pixels. Constrains the scroll container height.
	 * When omitted the table grows to fit its content.
	 */
	height?: number;
	/**
	 * Estimated row height in pixels used by the virtualizer.
	 * Tune this to match the actual rendered row height for accurate scrollbar behaviour.
	 * Default: 34.
	 */
	estimateRowHeight?: number;
	/**
	 * Number of extra rows rendered outside the visible area on each side.
	 * Higher values reduce blank-row flicker during fast scrolling at the cost of more DOM nodes.
	 * Default: 10.
	 */
	overscan?: number;
}

/**
 * A widget.
 *
 * When implementing a new widget type, extend this interface and
 *
 *   * add the identifier for the new type to AppBuilderWidgetType, and
 *   * define a new interface for the properties of the widget type and
 *     add it to the union type of "props".
 *
 * Node-level fields such as `actionSlots` are on this interface, not inside `props`.
 * @see [Action slots](../action-slots.md)
 */
export interface IAppBuilderWidget extends IAppBuilderNode {
	/** Type of the widget. */
	type: AppBuilderWidgetType;
	/** Properties of the widget. */
	props:
		| IAppBuilderWidgetPropsAccordion
		| IAppBuilderWidgetPropsText
		| IAppBuilderWidgetPropsImage
		| IAppBuilderWidgetPropsRoundChart
		| IAppBuilderWidgetPropsLineChart
		| IAppBuilderWidgetPropsAreaChart
		| IAppBuilderWidgetPropsBarChart
		| IAppBuilderWidgetPropsActions
		| IAppBuilderWidgetPropsAttributeVisualization
		| IAppBuilderWidgetPropsAgent
		| IAppBuilderWidgetPropsHostedAgent
		| IAppBuilderWidgetPropsIframe
		| IAppBuilderWidgetPropsProgress
		| IAppBuilderWidgetPropsDesktopClientSelection
		| IAppBuilderWidgetPropsDesktopClientOutputs
		| IAppBuilderWidgetPropsControls
		| IAppBuilderWidgetPropsForm
		| IAppBuilderWidgetPropsAccordionUi
		| IAppBuilderWidgetPropsSavedStates
		| IAppBuilderWidgetPropsSceneTreeExplorer
		| IAppBuilderWidgetPropsStackUi
		| IAppBuilderWidgetPropsTable;
}

/**
 * A tab displayed in a container.
 */
export interface IAppBuilderTab extends IAppBuilderNode {
	/** Name of the tab. */
	name: string;
	/** Optional icon name, image URL, or inline Iconify object of the tab. */
	icon?: IAppBuilderIcon;
	/** Optional tooltip. */
	tooltip?: string;
	/** Widgets displayed in the tab. */
	widgets: IAppBuilderWidget[];
}

/** Types of hints for containers */
export enum AppBuilderContainerNameType {
	Left = "left",
	Right = "right",
	Top = "top",
	Bottom = "bottom",
	Anchor3d = "anchor3d",
	Anchor2d = "anchor2d",
	Toolbar = "toolbar",
}

/**
 * Standard layout containers (not viewport anchors or toolbars).
 * Used by mobile fallbacks and the standard-container store.
 */
export type AppBuilderStandardContainerNameType =
	| AppBuilderContainerNameType.Left
	| AppBuilderContainerNameType.Right
	| AppBuilderContainerNameType.Top
	| AppBuilderContainerNameType.Bottom;

/**
 * Mobile fallback for a standard container.
 * Applied below `AppBuilderTemplateSelector.mobileBreakpoint` (theme default `"md"`).
 *
 * `disabled` hides the container without moving its content.
 * `container` moves tabs/widgets into that standard container and hides this one.
 * If both are set, `disabled` wins.
 */
export interface IAppBuilderStandardContainerMobileFallback {
	/** Hide this container on mobile. */
	disabled?: boolean;
	/** Standard container that receives this container’s content. */
	container?: AppBuilderStandardContainerNameType;
	/** Position relative to the target container’s native content. */
	position?: "before" | "after";
	/** Ordering when multiple containers fall back to the same target. */
	order?: number;
}

/** Optional props of a standard (left/right/top/bottom) container. */
export interface IAppBuilderStandardContainerProps {
	mobileFallback?: IAppBuilderStandardContainerMobileFallback;
}

/**
 * Theme-level mobile fallbacks keyed by source container (general default).
 * JSON `props.mobileFallback` overlays defined fields onto the theme entry
 * for that container (per-instance fine-tune). Omitted JSON keeps the theme.
 * Omitted JSON and theme keeps previous behavior (container stays put).
 */
export type IAppBuilderMobileFallbacks = {
	[K in AppBuilderStandardContainerNameType]?: IAppBuilderStandardContainerMobileFallback;
};

/**
 * Mobile fallback for a viewport anchor.
 * Applied below `AppBuilderTemplateSelector.mobileBreakpoint` unless the
 * anchor theme sets `mobileBreakpoint`. `disabled` hides without moving; omitted `container`
 * still targets `right` (previous behavior).
 */
export interface IAppBuilderViewportAnchorMobileFallback {
	/** Hide this anchor on mobile without injecting it into a container. */
	disabled?: boolean;
	/**
	 * Preview icon on mobile. If omitted, the original previewIcon is used.
	 */
	previewIcon?: IAppBuilderIcon;
	/** Standard container that receives this anchor’s content. */
	container?: AppBuilderStandardContainerNameType;
	/** Position relative to the target container’s native content. */
	position?: "before" | "after";
	/** Ordering when multiple extras fall back to the same target. */
	order?: number;
}

export type AppBuilderToolbarSide = "top" | "bottom" | "left" | "right";

export type AppBuilderToolbarAlign = "start" | "center" | "end";

export type AppBuilderToolbarVisibility = "always" | "onMouseActivity";

/** Type for the anchor containers */
export type AppBuilderAnchorContainerProperties = {
	/** Id of the container. */
	id: string;
	/** Optional justification of the container. (default: "MC") */
	justification?: TAG3D_JUSTIFICATION;
	/** Optional boolean to allow pointer events on the container. (default: true) */
	allowPointerEvents?: boolean;
	/** Optional icon name, image URL, or inline Iconify object shown to open the container. */
	previewIcon?: IAppBuilderIcon;
	/** Option to show a close button on the container. */
	useCloseButton?: boolean;
	/** Closing strategy if the anchor can be closed. */
	closingStrategy?: "button" | "emptyClick";
	/** Whether the anchor is exclusive with other anchors of the same type. Defaults to true; false when targeted by a setContainerVisibility action. */
	exclusive?: boolean;
	/** Whether the anchor is initially open. Defaults to !canBeHidden. */
	defaultOpen?: boolean;
	/** Optional width of the container. Can be either in px (e.g. 100 or "100px"), rem (e.g. 1.5rem), em (e.g. 1em), % (e.g. 100%) or calc() (e.g. calc(100% - 20px)) */
	width?: string | number;
	/** Optional height of the container. Can be either in px (e.g. 100 or "100px"), rem (e.g. 1.5rem), em (e.g. 1em), % (e.g. 100%) or calc() (e.g. calc(100% - 20px)) */
	height?: string | number;
	/** Optional maxWidth of the element. Can be either in px (e.g. 100 or "100px"), rem (e.g. 1.5rem), em (e.g. 1.5em), % (e.g. 100%) or calc (e.g. calc(100% - 20px)) */
	maxWidth?: string | number;
	/** Optional maxHeight of the element. Can be either in px (e.g. 100 or "100px"), rem (e.g. 1.5rem), em (e.g. 1.5em), % (e.g. 100%) or calc (e.g. calc(100% - 20px)) */
	maxHeight?: string | number;
	/** Option to use Paper component (default: true) */
	useContainer?: boolean;
	/**
	 * Options for the mobile fallback.
	 * Theme `ViewportAnchor2d` / `ViewportAnchor3d` `defaultProps.mobileFallback`
	 * is the general default; JSON overlays defined fields for this anchor.
	 */
	mobileFallback?: IAppBuilderViewportAnchorMobileFallback;
	/** Optional selection options. These options replace the behavior of the previewIcon and show the corresponding Anchor when the selection is active. (default: undefined) */
	selectionProperties?: Omit<
		ISelectionParameterProps,
		"minimumSelection" | "maximumSelection" | "deselectOnEmpty" | "prompt"
	>;
};

/** Type for the anchor 2d containers */
export type AppBuilderAnchor2dContainerProperties = {
	/**
	 * 2D location. When omitted, the panel docks to the DOM node of a
	 * setContainerVisibility action that targets this container id.
	 */
	location?: (string | number)[];
	/** Optional boolean to allow dragging of the container. (default: true) */
	draggable?: boolean;
} & AppBuilderAnchorContainerProperties;

/** Type for the anchor 3d containers */
export type AppBuilderAnchor3dContainerProperties = {
	/** 3D location */
	location: number[];
	/** Option to make the anchor hideable by geometry in the scene (default: false) */
	hideable?: boolean;
} & AppBuilderAnchorContainerProperties;

export type AppBuilderToolbarItemType =
	| AppBuilderControlType
	| "actionMenu"
	| "widgets"
	| "tabs";

export interface IAppBuilderToolbarItemBase<
	TType extends AppBuilderToolbarItemType,
	TProps extends object,
> extends IAppBuilderNode {
	/** Discriminator for toolbar item rendering and validation. */
	type: TType;
	/** Item-specific payload. */
	props: TProps;
	/** Optional stable id for runtime APIs, accessibility and diagnostics. */
	id?: string;
	/** Optional item order for runtime-merged groups. */
	order?: number;
	/** Optional presentation mode when this item is rendered inside a popover. */
	presentation?: "button" | "item";
}

export type IAppBuilderToolbarParameterItem = IAppBuilderToolbarItemBase<
	"parameter",
	IAppBuilderControlParameterRef
>;

export type IAppBuilderToolbarExportItem = IAppBuilderToolbarItemBase<
	"export",
	IAppBuilderControlExportRef
>;

export type IAppBuilderToolbarOutputItem = IAppBuilderToolbarItemBase<
	"output",
	IAppBuilderControlOutputRef
>;

export type IAppBuilderToolbarActionItem = IAppBuilderToolbarItemBase<
	"action",
	IAppBuilderControlActionRef
>;

export type IAppBuilderToolbarControlItem =
	| IAppBuilderToolbarParameterItem
	| IAppBuilderToolbarExportItem
	| IAppBuilderToolbarOutputItem
	| IAppBuilderToolbarActionItem;

export type IAppBuilderToolbarActionMenuItem = IAppBuilderToolbarItemBase<
	"actionMenu",
	IAppBuilderControlPresentation & {
		/** Action sections rendered when this item is opened. */
		sections: IAppBuilderToolbarActionItem[][];
	}
>;

export type IAppBuilderToolbarWidgetPanelItem = IAppBuilderToolbarItemBase<
	"widgets",
	IAppBuilderControlPresentation & {
		/** Widgets rendered when this item is opened. */
		widgets: IAppBuilderWidget[];
	}
>;

export type IAppBuilderToolbarTabbedPanelItem = IAppBuilderToolbarItemBase<
	"tabs",
	IAppBuilderControlPresentation & {
		/** Tabs rendered when this item is opened. */
		tabs: IAppBuilderTab[];
		/** When true, tabs stick to the top when scrolling the opened item content. */
		stickyTabs?: boolean;
	}
>;

export type IAppBuilderToolbarItem =
	| IAppBuilderToolbarControlItem
	| IAppBuilderToolbarActionMenuItem
	| IAppBuilderToolbarWidgetPanelItem
	| IAppBuilderToolbarTabbedPanelItem;

export type IAppBuilderToolbarGroups = IAppBuilderToolbarItem[][];

export interface IAppBuilderToolbarContainerProperties {
	/** Stable id used by runtime APIs and accessibility. */
	id: string;
	/** Screen edge. */
	side?: AppBuilderToolbarSide;
	/** Alignment along the selected edge. */
	align?: AppBuilderToolbarAlign;
	/** Lower values render first / closer to the edge. */
	order?: number;
	/** Visibility behavior. */
	visibility?: AppBuilderToolbarVisibility;
	/** Whether the toolbar is initially open. Defaults to true. */
	defaultOpen?: boolean;
	/**
	 * When true, opening a 2d or 3d container targeted by this toolbar
	 * closes the other containers this toolbar targets (both 2d and 3d).
	 * Closing or toggling a container off does not close the others.
	 * Defaults to true. Set to false to keep several containers open at once.
	 */
	exclusiveContainers?: boolean;
}

export interface IAppBuilderStandardContainer extends IAppBuilderNode {
	/** Name of the container. */
	name: AppBuilderStandardContainerNameType;
	/** Optional container props, including mobile fallback. */
	props?: IAppBuilderStandardContainerProps;
	/** Tabs displayed in the container. */
	tabs?: IAppBuilderTab[];
	/** When true, tabs stick to the top when scrolling the container content. */
	stickyTabs?: boolean;
	/** Further widgets displayed in the container. */
	widgets?: IAppBuilderWidget[];
}

export interface IAppBuilderAnchor2dContainer extends IAppBuilderNode {
	/** Name of the container. */
	name: AppBuilderContainerNameType.Anchor2d;
	/** Anchor-specific props. */
	props: AppBuilderAnchor2dContainerProperties;
	/** Tabs displayed in the container. */
	tabs?: IAppBuilderTab[];
	/** When true, tabs stick to the top when scrolling the container content. */
	stickyTabs?: boolean;
	/** Further widgets displayed in the container. */
	widgets?: IAppBuilderWidget[];
}

export interface IAppBuilderAnchor3dContainer extends IAppBuilderNode {
	/** Name of the container. */
	name: AppBuilderContainerNameType.Anchor3d;
	/** Anchor-specific props. */
	props: AppBuilderAnchor3dContainerProperties;
	/** Tabs displayed in the container. */
	tabs?: IAppBuilderTab[];
	/** When true, tabs stick to the top when scrolling the container content. */
	stickyTabs?: boolean;
	/** Further widgets displayed in the container. */
	widgets?: IAppBuilderWidget[];
}

export interface IAppBuilderToolbarContainer extends IAppBuilderNode {
	/** Name of the container. */
	name: AppBuilderContainerNameType.Toolbar;
	/** Toolbar-specific props. */
	props: IAppBuilderToolbarContainerProperties;
	/** Branch/group-preserving toolbar content. Outer array = groups, inner array = items within a group. */
	groups?: IAppBuilderToolbarGroups;
}

/**
 * A container for UI elements.
 *
 * The container type is discriminated by `name`, while the specific fields are
 * split per container kind.
 */
export type IAppBuilderContainer =
	| IAppBuilderStandardContainer
	| IAppBuilderAnchor2dContainer
	| IAppBuilderAnchor3dContainer
	| IAppBuilderToolbarContainer;

/** Types of actions to be executed after updating outputs. */
export type AppBuilderOutputActionsType = "setParameterValue";

/**
 * Properties of a "setParameterValue" output action.
 *
 * The returned parameter value is returned in the following format:
 * {
 *   [instanceId: string]: {
 *     [output: string]: ResOutputContent[] | undefined
 *   }
 * }
 * where the key `instanceId` is the name of the instance (if provided) or the identifier `instance[i]`
 * with i being the index of the instance in the instances array.
 *
 * The key `output` is the displayname/name/id of the output specified in the output action.
 *
 * The value is the array of ResOutputContent returned by the output.
 */
export interface IAppBuilderOutputActionsPropsSetParameterValue {
	/** The displayname/name/id of the output to check for updates. */
	output: string;
	/** The displayname/name/id of the parameter that should be set based on the value of the output. */
	parameter: string;
}

/**
 * Fields shared by additional sessions and instances on the App Builder data output.
 */
export interface IAppBuilderSessionDefinitionBase {
	/**
	 * Id of the session.
	 * When `slug` is omitted, this id is used as the platform model slug.
	 */
	sessionId: string;
	/**
	 * Platform model slug used to resolve the session.
	 * When omitted, `sessionId` is used as the slug.
	 */
	slug?: string;
}

/**
 * Instance created from the App Builder data output.
 * Used to customize a session by setting parameters and transformations.
 */
export interface IAppBuilderInstanceDefinition extends IAppBuilderSessionDefinitionBase {
	/**
	 * Parameter set for the instance.
	 * Defined in a parameter dictionary where the key is either the displayname, the name or the id of the parameter.
	 * The value is the parameter value.
	 * If none is provided, the default parameter set is used.
	 **/
	parameterValues?: {
		[key: string]: IAppBuilderParameterValueDefinition;
	};
	/** Optional name of the instance. This name will be used for the node in the scene graph, e.g. NAME_transformations_0 for the first transformation. */
	name?: string;
	/** Transformations for the instances, e.g. to position them in the scene. */
	transformations?: number[][];
	/** The actions that should be executed after an output of the instance has been updated. */
	outputActions?: {
		/** The type of output action that should be used. */
		type: AppBuilderOutputActionsType;
		/** Properties of the output action. */
		props: IAppBuilderOutputActionsPropsSetParameterValue;
	}[];
}

/**
 * Additional session created from the App Builder data output.
 * A full session whose parameters and exports are registered.
 */
export interface IAppBuilderSessionDefinition extends IAppBuilderSessionDefinitionBase {
	/**
	 * Optional initial parameter values, matching theme sessions.
	 * The key is the displayname, name, or id of the parameter.
	 * If none is provided, the default parameter set is used.
	 */
	initialParameterValues?: {[key: string]: string};
}

/**
 * Web app definition.
 * This is the root of the custom UI definition.
 *
 * Application events (`appready`, computation, export, interaction
 * select/hover) are assigned here. Several slots for one event use an
 * array (different `eventProps`). Pointer/`click` on the root attach to
 * the viewport host.
 *
 * @see [Action slots](../action-slots.md)
 */
export interface IAppBuilder {
	/**
	 * Application events, plus pointer/click/contextmenu for the viewport
	 * host, and `custom:*`. Zod rejects event names and `eventProps` the
	 * root cannot use.
	 *
	 * @see [Action slots](../action-slots.md)
	 */
	actionSlots?: IAppBuilderRootActionSlots;

	/** Version of the schema. */
	version: "1.0";

	/**
	 * Optional list of custom parameters that can be referenced
	 * in addition to parameters of the model.
	 */
	parameters?: IAppBuilderParameterDefinition[];

	/** Optional id of the session to use for defining custom parameters. */
	sessionId?: string;

	/**
	 * Containers to be displayed.
	 */
	containers: IAppBuilderContainer[];

	/**
	 * Optional list of instances to be created.
	 * Instances are used to customize a session by setting parameters and transformations.
	 */
	instances?: IAppBuilderInstanceDefinition[];

	/**
	 * Optional list of additional sessions to be created.
	 * These are full sessions in addition to the sessions from the theme or
	 * settings JSON. The controller session still comes from the theme or URL.
	 * A session id that also appears in `instances` is ignored. The instance
	 * owns that session.
	 */
	sessions?: IAppBuilderSessionDefinition[];

	/**
	 * Optional list of agents.
	 * For now only one agent is supported, but in the future we might support
	 * multiple agents, or allow agents to reference others as sub-agents.
	 * We do not consider parametric updates to the agent definition, i.e.,
	 * the agents get initialized on loading of the app, and updates to this
	 * property due to parameter changes are ignored.
	 */
	agents?: IAppBuilderAgent[];
}

/** assert default containers */
export function isStandardContainer(
	container: IAppBuilderContainer,
): container is IAppBuilderStandardContainer {
	return (
		container.name === AppBuilderContainerNameType.Left ||
		container.name === AppBuilderContainerNameType.Right ||
		container.name === AppBuilderContainerNameType.Top ||
		container.name === AppBuilderContainerNameType.Bottom
	);
}

/** assert anchor 2d container */
export function isAnchor2dContainer(
	container: IAppBuilderContainer,
): container is IAppBuilderAnchor2dContainer {
	return container.name === AppBuilderContainerNameType.Anchor2d;
}

/** assert anchor 3d container */
export function isAnchor3dContainer(
	container: IAppBuilderContainer,
): container is IAppBuilderAnchor3dContainer {
	return container.name === AppBuilderContainerNameType.Anchor3d;
}

/** assert toolbar container */
export function isToolbarContainer(
	container: IAppBuilderContainer,
): container is IAppBuilderToolbarContainer {
	return container.name === AppBuilderContainerNameType.Toolbar;
}

/** assert widget type "accordion" */
export function isAccordionWidget(
	widget: IAppBuilderWidget,
): widget is {type: "accordion"; props: IAppBuilderWidgetPropsAccordion} {
	return widget.type === "accordion";
}

/** assert widget type "text" */
export function isTextWidget(
	widget: IAppBuilderWidget,
): widget is {type: "text"; props: IAppBuilderWidgetPropsText} {
	return widget.type === "text";
}

/** assert widget type "image" */
export function isImageWidget(
	widget: IAppBuilderWidget,
): widget is {type: "image"; props: IAppBuilderWidgetPropsImage} {
	return widget.type === "image";
}

/** assert widget type "roundChart" */
export function isRoundChartWidget(
	widget: IAppBuilderWidget,
): widget is {type: "roundChart"; props: IAppBuilderWidgetPropsRoundChart} {
	return widget.type === "roundChart";
}

/** assert widget type "lineChart" */
export function isLineChartWidget(
	widget: IAppBuilderWidget,
): widget is {type: "lineChart"; props: IAppBuilderWidgetPropsLineChart} {
	return widget.type === "lineChart";
}

/** assert widget type "areaChart" */
export function isAreaChartWidget(
	widget: IAppBuilderWidget,
): widget is {type: "areaChart"; props: IAppBuilderWidgetPropsAreaChart} {
	return widget.type === "areaChart";
}

/** assert widget type "barChart" */
export function isBarChartWidget(
	widget: IAppBuilderWidget,
): widget is {type: "barChart"; props: IAppBuilderWidgetPropsBarChart} {
	return widget.type === "barChart";
}

/** assert widget type "actions" */
export function isActionsWidget(
	widget: IAppBuilderWidget,
): widget is {type: "actions"; props: IAppBuilderWidgetPropsActions} {
	return widget.type === "actions";
}

/** assert widget type "attributeVisualization" */
export function isAttributeVisualizationWidget(
	widget: IAppBuilderWidget,
): widget is {
	type: "attributeVisualization";
	props: IAppBuilderWidgetPropsAttributeVisualization;
} {
	return widget.type === "attributeVisualization";
}

/** assert widget type "agent" */
export function isAgentWidget(
	widget: IAppBuilderWidget,
): widget is {type: "agent"; props: IAppBuilderWidgetPropsAgent} {
	return widget.type === "agent";
}

/** assert widget type "hostedAgent" */
export function isHostedAgentWidget(
	widget: IAppBuilderWidget,
): widget is {type: "hostedAgent"; props: IAppBuilderWidgetPropsHostedAgent} {
	return widget.type === "hostedAgent";
}

/** assert widget type "iframe" */
export function isIframeWidget(
	widget: IAppBuilderWidget,
): widget is {type: "iframe"; props: IAppBuilderWidgetPropsIframe} {
	return widget.type === "iframe";
}

/** assert widget type "progress" */
export function isProgressWidget(
	widget: IAppBuilderWidget,
): widget is {type: "progress"; props: IAppBuilderWidgetPropsProgress} {
	return widget.type === "progress";
}

/** assert widget type "sceneTreeExplorer" */
export function isSceneTreeExplorerWidget(
	widget: IAppBuilderWidget,
): widget is {
	type: "sceneTreeExplorer";
	props: IAppBuilderWidgetPropsSceneTreeExplorer;
} {
	return widget.type === "sceneTreeExplorer";
}

/** assert widget type "desktopClientSelection" */
export function isDesktopClientSelectionWidget(
	widget: IAppBuilderWidget,
): widget is {
	type: "desktopClientSelection";
	props: IAppBuilderWidgetPropsDesktopClientSelection;
} {
	return widget.type === "desktopClientSelection";
}

/** assert widget type "desktopClientOutputs" */
export function isDesktopClientOutputsWidget(
	widget: IAppBuilderWidget,
): widget is {
	type: "desktopClientOutputs";
	props: IAppBuilderWidgetPropsDesktopClientOutputs;
} {
	return widget.type === "desktopClientOutputs";
}

/** assert widget type "controls" */
export function isControlsWidget(widget: IAppBuilderWidget): widget is {
	type: "controls";
	props: IAppBuilderWidgetPropsControls;
} {
	return widget.type === "controls";
}

/** assert widget type "form" */
export function isFormWidget(widget: IAppBuilderWidget): widget is {
	type: "form";
	props: IAppBuilderWidgetPropsForm;
} {
	return widget.type === "form";
}

/** assert widget type "accordionUi" */
export function isAccordionUiWidget(widget: IAppBuilderWidget): widget is {
	type: "accordionUi";
	props: IAppBuilderWidgetPropsAccordionUi;
} {
	return widget.type === "accordionUi";
}

/** assert widget type "savedStates" */
export function isSavedStatesWidget(widget: IAppBuilderWidget): widget is {
	type: "savedStates";
	props: IAppBuilderWidgetPropsSavedStates;
} {
	return widget.type === "savedStates";
}

/** assert widget type "stackUi" */
export function isStackUiWidget(widget: IAppBuilderWidget): widget is {
	type: "stackUi";
	props: IAppBuilderWidgetPropsStackUi;
} {
	return widget.type === "stackUi";
}

/** assert widget type "table" */
export function isTableWidget(widget: IAppBuilderWidget): widget is {
	type: "table";
	props: IAppBuilderWidgetPropsTable;
} {
	return widget.type === "table";
}

type AppBuilderControlLike = IAppBuilderControl | IAppBuilderToolbarItem;

/** assert control / toolbar item type "parameter" */
export function isParameterRefControl<T extends AppBuilderControlLike>(
	control: T,
): control is T & {
	type: "parameter";
	props: IAppBuilderControlParameterRef;
} {
	return control.type === "parameter";
}

/** assert control / toolbar item type "export" */
export function isExportRefControl<T extends AppBuilderControlLike>(
	control: T,
): control is T & {
	type: "export";
	props: IAppBuilderControlExportRef;
} {
	return control.type === "export";
}

/** assert control / toolbar item type "action" */
export function isActionRefControl<T extends AppBuilderControlLike>(
	control: T,
): control is T & {
	type: "action";
	props: IAppBuilderControlActionRef;
} {
	return control.type === "action";
}

/** assert control / toolbar item type "output" */
export function isOutputRefControl<T extends AppBuilderControlLike>(
	control: T,
): control is T & {
	type: "output";
	props: IAppBuilderControlOutputRef;
} {
	return control.type === "output";
}

/**
 * Settings for a session used by the AppBuilder.
 */
export interface IAppBuilderSettingsSession extends SessionCreationDefinition {
	/** Unique session id. Required for App Builder sessions. */
	id: string;
	/**
	 * Either slug and platformUrl, or ticket and modelViewUrl must be set.
	 */
	slug?: string;
	/**
	 * Either slug and platformUrl, or ticket and modelViewUrl must be set.
	 */
	platformUrl?: string;
	/**
	 * Set to true to require confirmation of the user to accept or reject changed parameter values.
	 */
	acceptRejectMode?: boolean;
	/**
	 * If the attribute visualization should be hidden by default.
	 */
	hideAttributeVisualization?: boolean;
	/**
	 * If the JSON menu should be hidden by default.
	 */
	hideJsonMenu?: boolean;
	/**
	 * If the saved states menu should be hidden by default.
	 */
	hideSavedStates?: boolean;
	/**
	 * If the desktop clients should be hidden by default.
	 */
	hideDesktopClients?: boolean;
	/**
	 * If the exports should be hidden by default.
	 */
	hideExports?: boolean;
	/**
	 * Whether to hide the default viewport toolbar. Defaults to false.
	 */
	hideDefaultToolbar?: boolean;
	/**
	 * In case we cannot connect to the platform, load settings from the viewer, if they were stored there. (default: undefined)
	 */
	loadPlatformSettingsFromViewer?: "platform" | "iframe";
	/**
	 * Optional model state id.
	 */
	modelStateId?: string;
	/**
	 * Optional callback for refreshing the JWT token.
	 */
	refreshJwtToken?: () => Promise<string>;
	/**
	 * Optional boolean to treat this sessions as an instance (default: false).
	 */
	instance?: boolean;
	/**
	 * If the session is an instance, optional property to delay loading of the instance until the first time it is used. (default: false)
	 */
	loadOnFirstUse?: boolean;
	/**
	 * If the session should be kept in the store after the first use (default: false)
	 */
	keepInStore?: boolean;
}

/**
 * Settings for a session used by the AppBuilder.
 */
export interface IAppBuilderSettingsJsonSession extends Omit<
	IAppBuilderSettingsSession,
	"modelViewUrl"
> {
	/**
	 * Override modelViewUrl to be optional.
	 */
	modelViewUrl?: string;
}

/**
 * AppBuilder-related settings.
 */
export interface IAppBuilderSettingsSettings {
	/**
	 * If true, hide the fallback AppBuilder containers which
	 * are shown in case no AppBuilder data output is found.
	 */
	disableFallbackUi?: boolean;
}

/**
 * Settings for initializing an AppBuilder application from a JSON file. This defines the sessions to create.
 */
export interface IAppBuilderSettingsJson {
	version: "1.0";
	/** Session to load. */
	sessions?: IAppBuilderSettingsJsonSession[];
	/** Settings */
	settings?: IAppBuilderSettingsSettings;
	/**
	 * Theme overrides
	 * @see https://mantine.dev/theming/theme-object/
	 */
	themeOverrides?: Record<string, any>;
	/**
	 * Optional AppBuilder definition, to be used instead of the
	 * AppBuilder output of the ShapeDiver model. This is useful
	 * for development.
	 */
	appBuilderOverride?: IAppBuilder;
	/**
	 * Optional agents list used instead of `IAppBuilder.agents` from the model
	 * (and instead of `appBuilderOverride.agents` if both are set).
	 * Useful for local development of agent features without replacing the
	 * rest of the App Builder output.
	 */
	agentOverride?: IAppBuilderAgent[];
}

/**
 * Settings for initializing an AppBuilder application. This defines the sessions to create.
 */
export interface IAppBuilderSettings extends IAppBuilderSettingsJson {
	/** Session to load. */
	sessions: IAppBuilderSettingsJsonSession[];
}

/**
 * Settings for initializing an AppBuilder application. This defines the sessions to create.
 */
export interface IAppBuilderSettingsResolved extends IAppBuilderSettings {
	/** Session to load. */
	sessions: IAppBuilderSettingsSession[];
}
